import * as Comlink from 'comlink';
import type { ServerTimeWorkerApi } from '@/common/composables/serverTime/serverTime.worker.types';

interface Subscriber {
  callback: (currentTime: Date) => void;
  interval: number;
  isActive: boolean;
  lastEmit: number;
}

export interface ServerTimeSubscription {
  /**
   * 구독을 활성화한다. `initialTime` 을 넘기면 본인 callback 을 즉시 1 회 호출해
   * 호출자가 워커 첫 정기 tick(interval 만큼 지연) 을 기다리지 않고 화면을 갱신할 수 있다.
   * 다른 구독자에게는 영향 없다 (자기 callback 만 호출 + lastEmit 갱신).
   */
  start: (initialTime?: Date) => Promise<void>;
  stop: () => Promise<void>;
  updateInterval: (newInterval: number) => Promise<void>;
  cleanup: () => void;
}

/**
 * Worker tick jitter 허용 오차 (ms).
 *
 * 모듈 싱글톤 Worker 는 가장 짧은 active interval 로 돌고, 각 구독자는 dispatchTick 에서
 * 자기 주기로 throttle 된다. 그런데 Worker postMessage / setInterval 은 항상 약간의 지연을
 * 동반하므로 (예: 5000ms 주기인데 실제로 4996ms 시점에 tick 도착) interval 경계에서
 * 한 주기를 통째로 건너뛰는 현상을 막기 위해 이만큼의 슬랙을 둔다.
 *
 * 값을 너무 크게 잡으면 짧은 interval 구독자에게 조기 호출이 발생하므로,
 * 일반 데스크탑 setInterval jitter 범위인 5ms 로 설정한다.
 */
const TICK_JITTER_TOLERANCE_MS = 5;

const subscribers = new Map<symbol, Subscriber>();

let worker: Worker | null = null;
let workerApi: Comlink.Remote<ServerTimeWorkerApi> | null = null;
let workerIsRunning = false;
let currentTickInterval = 0;

// 워커가 아직 없을 때 들어온 base time 을 보관한다. 워커는 reconcile(구독자 start) 경로의
// ensureWorker 에서만 생성되어야 "구독자 없이 살아 있는 워커" 누수가 없으므로, setServerTimeBase
// 는 워커를 직접 만들지 않고 여기에 적어 둔 뒤 다음 ensureWorker 시점에 주입한다.
let pendingBaseTime: string | null = null;

// dispatchTick 을 매 reconcile 마다 Comlink.proxy 로 감싸면 Comlink endpoint 에
// 이전 프록시 핸들러가 누적되어 메모리가 증가한다. 워커가 사용하는 콜백은 항상 동일하므로
// 모듈 스코프에서 단 한 번만 proxy 화 해서 재사용한다.
let dispatchTickProxy: ((currentTime: Date) => void) | null = null;

const ensureWorker = (): void => {
  if (worker) return;
  worker = new Worker(
    new URL('@/common/composables/serverTime/serverTime.worker.ts', import.meta.url),
    {
      type: 'module',
    },
  );
  workerApi = Comlink.wrap<ServerTimeWorkerApi>(worker);
  // 워커 생성 전에 setServerTimeBase 로 들어온 base time 이 있으면 새 워커에 즉시 주입한다.
  if (pendingBaseTime !== null) {
    void workerApi.setTime(pendingBaseTime);
  }
};

const terminateWorker = (): void => {
  workerApi?.[Comlink.releaseProxy]();
  worker?.terminate();
  worker = null;
  workerApi = null;
  workerIsRunning = false;
  currentTickInterval = 0;
  // 워커가 사라지면 endpoint 도 함께 사라지므로 다음 사이클에 새 프록시를 만든다.
  dispatchTickProxy = null;
};

const dispatchTick = (currentTime: Date): void => {
  const now = Date.now();
  subscribers.forEach((sub) => {
    if (!sub.isActive) return;
    if (now - sub.lastEmit + TICK_JITTER_TOLERANCE_MS >= sub.interval) {
      sub.lastEmit = now;
      // 한 구독자 콜백이 throw 해도 다른 구독자 tick 이 끊기지 않도록 격리한다.
      try {
        sub.callback(currentTime);
      } catch (e) {
        console.error('[serverTimeBridge] subscriber callback error:', e);
      }
    }
  });
};

const computeShortestActiveInterval = (): number => {
  let min = Number.POSITIVE_INFINITY;
  subscribers.forEach((sub) => {
    if (sub.isActive && sub.interval < min) min = sub.interval;
  });
  return Number.isFinite(min) ? min : 0;
};

const runReconcile = async (): Promise<void> => {
  const needed = computeShortestActiveInterval();

  if (needed === 0) {
    if (workerIsRunning) {
      await workerApi?.stop();
      workerIsRunning = false;
    }
    if (subscribers.size === 0) {
      terminateWorker();
    }
    return;
  }

  ensureWorker();
  if (!workerApi) return;

  if (!workerIsRunning || needed !== currentTickInterval) {
    currentTickInterval = needed;
    if (!dispatchTickProxy) {
      dispatchTickProxy = Comlink.proxy(dispatchTick);
    }
    await workerApi.start(needed, dispatchTickProxy);
    workerIsRunning = true;
  }
};

// reconcile 은 비동기이며 start/stop/cleanup 에서 자주 호출된다.
// 동시 실행 시 워커가 막 start 된 직후 다른 호출이 terminate 하는 등의 race 가 발생하므로
// 이전 호출이 끝난 뒤에 다음 호출이 이어지도록 직렬화한다. 이전 호출이 실패해도
// 다음 호출은 항상 진행되어야 하므로 catch 로 에러를 흡수한 뒤 then 으로 이어붙인다.
let reconcileChain: Promise<void> = Promise.resolve();
const reconcile = (): Promise<void> => {
  const next = reconcileChain.catch(() => undefined).then(runReconcile);
  // cleanup() 처럼 fire-and-forget 으로 호출되는 경우 next 의 rejection 이 묻혀 디버깅이
  // 어려워지므로 chain 용 사본에서 한 번 로깅한다. 호출자가 await 한 next 자체는
  // 그대로 reject 되므로 catch 가능.
  reconcileChain = next.catch((e) => {
    console.error('[serverTimeBridge] reconcile error:', e);
  });
  return next;
};

export const setServerTimeBase = async (timeString: string): Promise<void> => {
  // 워커를 새로 만들지 않는다. 워커가 이미 떠 있으면 즉시 반영하고, 없으면 보관만 했다가
  // 다음 ensureWorker(구독자 start) 때 주입한다. (구독자 없는 워커 잔존/race 누수 방지)
  pendingBaseTime = timeString;
  if (workerApi) await workerApi.setTime(timeString);
};

export const subscribeServerTime = (
  interval: number,
  callback: (currentTime: Date) => void,
): ServerTimeSubscription => {
  const id = Symbol('serverTimeSubscriber');
  subscribers.set(id, { callback, interval, isActive: false, lastEmit: 0 });

  return {
    start: async (initialTime?: Date) => {
      const sub = subscribers.get(id);
      if (!sub) return;
      sub.isActive = true;
      sub.lastEmit = 0;
      await reconcile();
      if (initialTime) {
        try {
          sub.callback(initialTime);
        } catch (e) {
          console.error('[serverTimeBridge] initial callback error:', e);
        }
        // callback 호출 직후 lastEmit 을 set 해 직후 도착할 워커 첫 tick 이 자기 throttle 안에서
        // 막혀 redundant 호출되지 않게 한다. reconcile / callback 사이의 await 시간차에 영향
        // 받지 않도록 정확한 발사 시점 기준으로 갱신.
        sub.lastEmit = Date.now();
      }
    },
    stop: async () => {
      const sub = subscribers.get(id);
      if (!sub) return;
      sub.isActive = false;
      await reconcile();
    },
    updateInterval: async (newInterval: number) => {
      const sub = subscribers.get(id);
      if (!sub) return;
      sub.interval = newInterval;
      sub.lastEmit = 0;
      if (sub.isActive) await reconcile();
    },
    cleanup: () => {
      subscribers.delete(id);
      void reconcile();
    },
  };
};
